import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
import { REST_API } from '../constants/defaultValues';

const getUserAvatarUrl = (avatarUrl) => {
  if (!avatarUrl) return '';
  if (avatarUrl === 'avatar.jpg') return '';
  if (avatarUrl.startsWith('/uploads/')) {
    return `${REST_API}${avatarUrl}`;
  }
  return avatarUrl;
};

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  selectedFacility: '10', // Default to 'Al Ikhtayar Al Qasmia' (facility_id=10)
  allowedModules: []
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      selectedFacility: action.payload.selectedFacility || state.selectedFacility,
      allowedModules: action.payload.allowedModules || []
    };
  },
  SET_FACILITY: (state, action) => ({
    ...state,
    selectedFacility: action.payload.selectedFacility,
    allowedModules: action.payload.allowedModules
  }),
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    allowedModules: []
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  setSelectedFacility: () => { }
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const response = await axios.get('/api/account/my-account');
          const { user } = response.data;

          const storedFacility = window.localStorage.getItem('selectedFacility') || '10';
          const rawIds = user.staffFacilityIds || user.staff_facility_ids;
          const allowedFacilities = rawIds ? String(rawIds).split(',') : null;
          let finalFacility = storedFacility;

          
          if (allowedFacilities && !allowedFacilities.includes(finalFacility)) {
            finalFacility = allowedFacilities[0] || '10';
            window.localStorage.setItem('selectedFacility', finalFacility);
          }

          let allowedModules = [];
          try {
            const modulesResponse = await axios.get(`/api/facilities/${finalFacility}/modules`);
            allowedModules = modulesResponse.data.modules ? modulesResponse.data.modules.split(',') : [];
          } catch (error) {
            console.error('Failed to fetch facility modules', error);
          }

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: {
                ...user,
                displayName: user.username || `${user.firstName} ${user.lastName}`,
                phoneNumber: user.phone,
                photoURL: getUserAvatarUrl(user.avatarUrl)
              },
              selectedFacility: finalFacility,
              allowedModules
            }
          });
        } else {
          const storedFacility = window.localStorage.getItem('selectedFacility') || '10';
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              selectedFacility: storedFacility,
              allowedModules: []
            }
          });
        }
      } catch (err) {
        console.error(err);
        const storedFacility = window.localStorage.getItem('selectedFacility') || '10';
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            selectedFacility: storedFacility,
            allowedModules: []
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/api/account/login', {
      email,
      password
    });
    const { accessToken, user } = response.data;

    const storedFacility = window.localStorage.getItem('selectedFacility') || '10';
    const rawIds = user.staffFacilityIds || user.staff_facility_ids;
    const allowedFacilities = rawIds ? String(rawIds).split(',') : null;
    let finalFacility = storedFacility;

    
    if (allowedFacilities && !allowedFacilities.includes(finalFacility)) {
      finalFacility = allowedFacilities[0] || '10';
      window.localStorage.setItem('selectedFacility', finalFacility);
    }

    let allowedModules = [];
    try {
      const modulesResponse = await axios.get(`/api/facilities/${finalFacility}/modules`);
      allowedModules = modulesResponse.data.modules ? modulesResponse.data.modules.split(',') : [];
    } catch (error) {
      console.error('Failed to fetch facility modules', error);
    }

    setSession(accessToken);
    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user: {
          ...user,
          displayName: user.username || `${user.firstName} ${user.lastName}`,
          phoneNumber: user.phone,
          photoURL: getUserAvatarUrl(user.avatarUrl)
        },
        selectedFacility: finalFacility,
        allowedModules
      }
    });
  };

  // UPDATED: Added username parameter and included it in the axios post body
  const register = async (email, password, firstName, lastName, username) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
      username // Now properly sending to the backend
    });
    const { accessToken, user } = response.data;

    const storedFacility = window.localStorage.getItem('selectedFacility') || '10';
    let allowedModules = [];
    try {
      const modulesResponse = await axios.get(`/api/facilities/${storedFacility}/modules`);
      allowedModules = modulesResponse.data.modules ? modulesResponse.data.modules.split(',') : [];
    } catch (error) {
      console.error('Failed to fetch facility modules', error);
    }

    setSession(accessToken);
    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user: {
          ...user,
          displayName: user.username || `${user.firstName} ${user.lastName}`,
          phoneNumber: user.phone,
          photoURL: getUserAvatarUrl(user.avatarUrl)
        },
        selectedFacility: storedFacility,
        allowedModules
      }
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const resetPassword = async (email) => {
    const response = await axios.post('/api/account/reset-password', { email });
    return response.data;
  };

  const setSelectedFacility = async (facilityId) => {
    window.localStorage.setItem('selectedFacility', facilityId);
    let allowedModules = [];
    try {
      const modulesResponse = await axios.get(`/api/facilities/${facilityId}/modules`);
      allowedModules = modulesResponse.data.modules ? modulesResponse.data.modules.split(',') : [];
    } catch (error) {
      console.error('Failed to fetch facility modules', error);
    }

    dispatch({
      type: 'SET_FACILITY',
      payload: {
        selectedFacility: facilityId,
        allowedModules
      }
    });
  };

  const updateProfile = async (values) => {
    // This is now handled by AccountStaffInfo calling the specific staff update endpoint
    // We just need to update the local state with fresh data
    const response = await axios.get('/api/account/my-account');
    const { user: updatedUser } = response.data;

    const storedFacility = window.localStorage.getItem('selectedFacility') || '10';
    const rawIds = updatedUser.staffFacilityIds || updatedUser.staff_facility_ids;
    const allowedFacilities = rawIds ? String(rawIds).split(',') : null;
    let finalFacility = storedFacility;

    if (allowedFacilities && !allowedFacilities.includes(finalFacility)) {
      finalFacility = allowedFacilities[0] || '10';
      window.localStorage.setItem('selectedFacility', finalFacility);
    }

    let allowedModules = [];
    try {
      const modulesResponse = await axios.get(`/api/facilities/${finalFacility}/modules`);
      allowedModules = modulesResponse.data.modules ? modulesResponse.data.modules.split(',') : [];
    } catch (error) {
      console.error('Failed to fetch facility modules', error);
    }

    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user: {
          ...state.user,
          ...updatedUser,
          displayName: updatedUser.username || `${updatedUser.firstName} ${updatedUser.lastName}`,
          phoneNumber: updatedUser.phone,
          photoURL: getUserAvatarUrl(updatedUser.avatarUrl)
        },
        selectedFacility: finalFacility,
        allowedModules
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        resetPassword,
        setSelectedFacility,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };